import Decimal from 'decimal.js';

import appSettings from '../config/settings';
import { types as sdkTypes } from './sdkLoader';
import { convertMoneyToNumber, convertUnitToSubUnit } from './currency';

const { Money } = sdkTypes;
const { subUnitDivisors } = appSettings;

// Relative to USD; configurable via hosted/local app config.
const DEFAULT_CONVERSION_RATES = {
  AUD: 1.53,
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
};

const getRates = config => {
  const configuredRates = config?.currencyConversionRates;
  return configuredRates && typeof configuredRates === 'object'
    ? { ...DEFAULT_CONVERSION_RATES, ...configuredRates }
    : DEFAULT_CONVERSION_RATES;
};

const hasRequiredRate = (rates, currency) => typeof rates[currency] === 'number' && rates[currency] > 0;

/**
 * Convert Money value to target currency using configured exchange rates.
 *
 * @param {import('sharetribe-flex-sdk').Money} money
 * @param {string} targetCurrency
 * @param {Object} [config]
 * @returns {import('sharetribe-flex-sdk').Money | null}
 */
export const convertMoneyToCurrency = (money, targetCurrency, config = {}) => {
  if (!(money instanceof Money) || !targetCurrency) {
    return null;
  }

  if (money.currency === targetCurrency) {
    return money;
  }

  if (!subUnitDivisors[targetCurrency]) {
    return null;
  }

  const rates = getRates(config);
  if (!hasRequiredRate(rates, money.currency) || !hasRequiredRate(rates, targetCurrency)) {
    return null;
  }

  const sourceMajorUnits = new Decimal(convertMoneyToNumber(money));
  const usdValue = sourceMajorUnits.dividedBy(new Decimal(rates[money.currency]));
  const targetMajorUnits = usdValue.times(new Decimal(rates[targetCurrency]));

  try {
    const targetSubUnits = convertUnitToSubUnit(targetMajorUnits.toFixed(2), subUnitDivisors[targetCurrency]);
    return new Money(targetSubUnits, targetCurrency);
  } catch (e) {
    return null;
  }
};

